import { useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { TenantContext } from '@interxfi/api-client';
import { useActiveProgramId, useRealmAuthContext } from '@interxfi/api-client/context';
import { useGetProgram } from '@interxfi/api-client/program/get-program';
import { useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { PlainIconButton } from '@interxfi/design-system/components/button';
import { EmptyIllustration } from '@interxfi/design-system/components/illustration/empty-illustration';
import { DEFAULT_LAYOUT_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import { TextLink } from '@interxfi/design-system/components/link/text-link/text-link';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import { BaseTypography, Typography } from '@interxfi/design-system/components/typography';
import { TypographyProvider } from '@interxfi/design-system/components/typography/context';
import CompanyIcon from '@interxfi/design-system/icons/react/icon-company';
import InfoIcon from '@interxfi/design-system/icons/react/icon-info';
import {
  getOneOfType,
  type IxcRegistrationV1AccountQueryServiceAccountListAccountListRequest,
  IxcRegistrationV1AccountQueryServiceAccountListFilterableFields,
  type IxmUserauthV1ProgramRegistrationTypeOneOfValue,
} from '@interxfi/types';
import { RealmPill } from '../../../../components/realm/realm-pill';
import { DevelopersSection } from '../../../../features/docs/developers-section';
import { CorrespondentAccountDescriptionDrawer } from '../../../../features/registration/account/correspondent-account-description-drawer';
import { PlatformAccountTileGrid } from '../../../../features/registration/account/platform-account-tile/platform-account-tile-grid';
import { getProgramConfigurationPath, PROGRAM_BASE_PATH } from '../../../../features/userauth/program/program-routing';
import type { RouteGuardOptions } from '../../../../util/routing/guard';
import { getPageHead } from '../../../../util/routing/meta';
import gridContainerStyles from '../../../../features/docs/grid-container/grid-container.module.scss';
import styles from './dashboard.module.scss';

export const Route = createFileRoute('/_app/_clearing-core/dashboard/')({
  staticData: {
    guard: { access: { allowedTenantTypes: [TenantContext.Program] } },
  } as const satisfies RouteGuardOptions,
  loader: () =>
    ({
      breadcrumb: buildBreadcrumb({
        id: 'dashboard',
        label: 'Dashboard',
        to: '/dashboard',
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Dashboard'),
  component: Dashboard,
});

function buildCorrespondentAccountQuery(
  programId: string | undefined,
  programRegistrationType: IxmUserauthV1ProgramRegistrationTypeOneOfValue | undefined,
): IxcRegistrationV1AccountQueryServiceAccountListAccountListRequest | undefined {
  if (!programId || !programRegistrationType || programRegistrationType === 'none') {
    return undefined;
  }

  return {
    query: {
      filters: [
        {
          field: {
            name: IxcRegistrationV1AccountQueryServiceAccountListFilterableFields.DataSpecType,
            type: {
              value: 'platform',
            },
          },
        },
        {
          field: {
            name: IxcRegistrationV1AccountQueryServiceAccountListFilterableFields.DataSpecPlatformOwnerType,
            type: {
              value: 'program',
            },
          },
        },
        {
          field: {
            name: IxcRegistrationV1AccountQueryServiceAccountListFilterableFields.ProgramId,
            type: {
              value: programId,
            },
          },
        },
      ],
    },
  };
}

const typographyProps = {
  lineHeight: '1.5rem',
  size: '0.875rem',
  tone: 'neutral-dk',
} as const;

const welcomeParagraphTypography = {
  lineHeight: '1.5rem',
  size: '1rem',
  weight: 'regular',
} as const;

const developersSectionPadding = { bottom: '96px', top: '64px' } as const;

const paragraphStyles = { margin: 0 };

function Dashboard() {
  const userContext = useRealmAuthContext();
  const { activeRealm, activeRealmIsSimulated } = useActiveRealmAPIContext();
  const activeProgramId = useActiveProgramId();
  const { data } = useGetProgram(activeProgramId ? { programId: activeProgramId } : undefined);
  const registrationType = getOneOfType(data?.program?.data?.details?.registrationType);
  const query = useMemo(() => buildCorrespondentAccountQuery(activeProgramId, registrationType), [activeProgramId, registrationType]);
  const hasNoRegistrationType = registrationType === 'none';
  const realmName = activeRealm?.data?.spec?.name;

  return (
    <PageLayout
      gap={DEFAULT_PAGE_LAYOUT_GAP}
      maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}
      padding={{ ...DEFAULT_LAYOUT_CONTAINER_PADDING, bottom: '0' }}
    >
      {hasNoRegistrationType && (
        <PageLayout.Section noBorder>
          <EmptyIllustration
            icon={<CompanyIcon aria-hidden />}
            message="No Correspondent Accounts"
            subMessage={
              <Typography {...typographyProps}>
                To generate accounts, assign a Registration Type in{' '}
                <TextLink
                  LinkElement={Link}
                  to={activeProgramId ? getProgramConfigurationPath(activeProgramId) : PROGRAM_BASE_PATH}
                  typography={typographyProps}
                >
                  Program Configuration
                </TextLink>
              </Typography>
            }
          />
        </PageLayout.Section>
      )}

      {!hasNoRegistrationType && (
        <PageLayout.Section noBorder>
          <VerticalStack gap={7}>
            <Typography lineHeight="2rem" size="1.5rem" variant="heading" weight="bold">
              {userContext?.data?.profile?.firstName ? (
                ['Welcome', userContext.data.profile.firstName].filter(Boolean).join(', ')
              ) : (
                <Skeleton charCount={20} />
              )}
            </Typography>

            <div className={styles.welcomeText}>
              <TypographyProvider value={welcomeParagraphTypography}>
                <BaseTypography style={paragraphStyles} tone="neutral-dk" weight="regular">
                  You're currently building in <RealmPill realmName={realmName} />.
                  {activeRealmIsSimulated ? (
                    <>
                      {' To request access to a live environment, please email '}
                      <a href="mailto:sales@interchange.com">sales@interchange.com</a>.
                    </>
                  ) : null}
                </BaseTypography>
              </TypographyProvider>
            </div>
          </VerticalStack>
        </PageLayout.Section>
      )}

      {!hasNoRegistrationType && query ? (
        <PageLayout.Section key={activeProgramId} noBorder>
          <VerticalStack gap={7}>
            <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
              <Typography lineHeight="1.625rem" size="1.125rem" variant="heading">
                Correspondent Accounts
              </Typography>

              <CorrespondentAccountDescriptionDrawer
                trigger={
                  <PlainIconButton aria-label="View more information about correspondent accounts">
                    <InfoIcon aria-hidden width={16} />
                  </PlainIconButton>
                }
              />
            </HorizontalStack>

            <PlatformAccountTileGrid loadingTiles={6} query={query} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {!hasNoRegistrationType && (
        <PageLayout.Section className={gridContainerStyles.gridContainer} noBorder>
          <DevelopersSection padding={developersSectionPadding} />
        </PageLayout.Section>
      )}
    </PageLayout>
  );
}
